import React from "react";
import { Autocomplete, AutocompleteRenderInputParams, SxProps, TextField, Theme } from "@mui/material";
import {
  assignableOwners as AssignableOwnersResult,
  assignableOwners_assignableOwners as AssignableOwnerNode,
} from "types/gql-types";
import OwnerChip from "./owner-chip";
import { profileOwnerLabel } from "./owner-names";

// Shared owner picker (M2-T07): the bulk Assign-Owner modal and the inline owner
// cell consume the SAME grouped / "Me"-pinned selector and option helpers.
//
// The owner DATA type is the codegen result of the ASSIGNABLE_OWNERS query.
export type AssignableOwnersData = AssignableOwnersResult;

// `AssignableOwner` is the codegen owner-list item, but with the schema-non-null
// fields the picker helpers defensively null-guard widened to nullable: the
// resolver can return null for `profile`/`property` and an empty/absent list for
// `interestLists` at runtime (e.g. an org-wide owner managing no property), which
// the helpers below and their tests exercise. `id` and the profile name fields
// stay anchored to the generated shape.
type GeneratedProfile = AssignableOwnerNode["profile"];
type OwnerProfile = Pick<GeneratedProfile, "firstName" | "lastName" | "fullName">;
type OwnerListItem = { id: string; property: { id?: string; name: string | null } | null };

export type AssignableOwner =
  Pick<AssignableOwnerNode, "id"> & {
    profile: OwnerProfile | null;
    // Managed properties — grouping basis for the picker (HAV-55). Applications
    // (`interests`) are not selected by ASSIGNABLE_OWNERS (HAV-188).
    interestLists?: OwnerListItem[] | null;
  };

// Codegen models `assignableOwners` as `(owner | null)[] | null` (schema
// nullability). The picker helpers want a flat non-null `AssignableOwner[]`, so
// every caller funnels the query data through here.
export const ownersFromData = (data: AssignableOwnersData | undefined): AssignableOwner[] =>
  (data?.assignableOwners ?? []).filter(
    (owner): owner is AssignableOwnerNode => owner !== null,
  );

export type OwnerOption = {
  userId: string;
  label: string;
  // Property-team group label shown by Autocomplete's groupBy.
  group: string;
  // Sort key so "Me" (group "") pins to the very top, ahead of real groups.
  isMe: boolean;
};

const ME_GROUP = "";

// Owners are grouped by the properties they MANAGE (interestLists -> property.name,
// HAV-55), listed once per managed property so they're findable under each team.
// Owners managing no property fall under this catch-all (the org name isn't on the
// payload).
const NO_PROPERTY_GROUP = "Organization-wide";

export const ownerPropertyGroups = (owner: AssignableOwner): string[] => {
  const names = (owner.interestLists ?? [])
    .map((managed) => managed.property?.name)
    .filter((name): name is string => Boolean(name));
  const unique = Array.from(new Set(names));
  return unique.length > 0 ? unique : [NO_PROPERTY_GROUP];
};

// HAV-59 — the single source of truth for "list id -> property.name", consumed by
// both the bulk modal and the inline owner cell so they float the same group.
// Keying off list id (not name) fixes the case where a waitlist's name differs
// from its property's name.
export const buildInterestListNameMap = (owners: AssignableOwner[]): Map<string, string> => {
  const map = new Map<string, string>();
  owners.forEach((owner) => {
    (owner.interestLists ?? []).forEach((list) => {
      const listId = list.id;
      const name = list.property?.name;
      if (listId && name && !map.has(listId)) map.set(listId, name);
    });
  });
  return map;
};

// Access is checked on both front end and back end; this is the client-side UX
// gate behind the access-grant warning. HAV-56: read interestLists (managed), NOT
// interests (staff owners have none, which wrongly fired the warning for all).
export const ownerManagesAll = (owner: AssignableOwner, interestListIds: string[]): boolean => {
  const managed = new Set((owner.interestLists ?? []).map((list) => list.id));
  return interestListIds.every((id) => managed.has(id));
};

const UNKNOWN_PROPERTY_NAME = "this property";

// The property names an access grant would cover, for the warning copy; a list
// whose property isn't known reads "this property".
export const accessGrantPropertyName = (
  interestListIds: string[],
  nameMap: Map<string, string>,
): string => Array.from(
  new Set(interestListIds.map((id) => nameMap.get(id) ?? UNKNOWN_PROPERTY_NAME)),
).join(", ");

// HAV-59 — resolves the active list id to its property name; null for "all" or an
// unresolved id, which leaves the picker alphabetical.
export const resolveCurrentPropertyName = (
  owners: AssignableOwner[],
  interestListId: string | null | undefined,
): string | null => {
  if (!interestListId || interestListId === "all") return null;
  return buildInterestListNameMap(owners).get(interestListId) ?? null;
};

// HAV-58 — group ordering rank when a current property is in scope (lower sorts
// first): current property at top, org-wide catch-all last, others in between.
const groupRank = (group: string, currentPropertyName: string): number => {
  if (group === currentPropertyName) return 0;
  if (group === NO_PROPERTY_GROUP) return 2;
  return 1;
};

export const buildOptions = (
  owners: AssignableOwner[],
  currentUserId: string,
  currentUserName: string,
  // HAV-58 — when set, that property's group floats to the top (under "Me"); falsy
  // preserves pure-alphabetical order (the owner-filter + slideout callers).
  currentPropertyName?: string | null,
): OwnerOption[] => {
  const options: OwnerOption[] = [];

  owners.forEach((owner) => {
    if (owner.id === currentUserId) return; // pinned separately as "Me"
    const label = profileOwnerLabel(owner.profile);
    ownerPropertyGroups(owner).forEach((group) => {
      options.push({ userId: owner.id, label, group, isMe: false });
    });
  });

  if (currentPropertyName) {
    options.sort(
      (a, b) =>
        groupRank(a.group, currentPropertyName) - groupRank(b.group, currentPropertyName) ||
        a.group.localeCompare(b.group) ||
        a.label.localeCompare(b.label),
    );
  } else {
    options.sort(
      (a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label),
    );
  }

  // Pin "Me" at the very top (empty group sorts first).
  const me = owners.find((owner) => owner.id === currentUserId);
  if (me) {
    options.unshift({
      userId: currentUserId,
      label: `Me (${currentUserName})`,
      group: ME_GROUP,
      isMe: true,
    });
  }

  return options;
};

type CommonOwnerSelectProps = {

  /** Pre-built options (see buildOptions); grouped by `group` unless `grouped` is false. */
  options: OwnerOption[];
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  autoFocus?: boolean;
  grouped?: boolean;
};

type SingleOwnerSelectProps = CommonOwnerSelectProps & {
  multiple?: false;
  value: OwnerOption | null;
  onChange: (value: OwnerOption | null) => void;
};

type MultipleOwnerSelectProps = CommonOwnerSelectProps & {
  multiple: true;
  value: OwnerOption[];
  onChange: (value: OwnerOption[]) => void;

  /** Caps how many owners can be chosen; further options are disabled at the cap. */
  maxSelections?: number;

  /** Hides owners who are already chosen, so removal happens from the chips. */
  filterSelectedOptions?: boolean;

  sx?: SxProps<Theme>;
  size?: "small" | "medium";
  required?: boolean;

  /** Lifts the option list above a Popover/Dialog, which share the modal z-index. */
  listAboveModal?: boolean;

  chipDataCy?: (option: OwnerOption) => string;
  deleteDataCy?: (option: OwnerOption) => string;
  deleteAriaLabel?: (option: OwnerOption) => string;

  /** Chips for these owners stay, but can't be removed while their change is in flight. */
  disabledUserIds?: ReadonlySet<string>;
};

export type OwnerSelectProps = SingleOwnerSelectProps | MultipleOwnerSelectProps;

// MUI keys options by label otherwise, so same-named owners (or several "Unnamed
// owner"s) would collide. userId alone repeats too: an owner is listed once per
// property group they manage, so the group is part of the key.
export const ownerOptionKey = (option: OwnerOption) => `${option.group}\u0000${option.userId}`;

const renderOwnerInput = (
  label: string,
  autoFocus: boolean,
  helperText?: string,
  required = true,
) =>
  (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={label}
      placeholder="Search users..."
      autoFocus={autoFocus}
      helperText={helperText}
      required={required}
    />
  );

const MultipleOwnerSelect: React.FC<MultipleOwnerSelectProps> = ({
  options,
  value,
  onChange,
  loading = false,
  disabled = false,
  id = "assign-owner-select",
  autoFocus = false,
  grouped = true,
  maxSelections,
  filterSelectedOptions = false,
  sx,
  size,
  required = true,
  listAboveModal = false,
  chipDataCy,
  deleteDataCy,
  deleteAriaLabel,
  disabledUserIds,
}) => {
  const selectedIds = new Set(value.map((option) => option.userId));
  const atCap = maxSelections !== undefined && value.length >= maxSelections;

  return (
    <Autocomplete<OwnerOption, true>
      multiple
      id={id}
      options={options}
      loading={loading}
      value={value}
      disabled={disabled}
      disableCloseOnSelect
      filterSelectedOptions={filterSelectedOptions}
      size={size}
      sx={sx}
      getOptionLabel={(option) => option.label}
      getOptionKey={ownerOptionKey}
      groupBy={grouped ? (option) => option.group : undefined}
      // An owner listed under several property groups is ONE choice: matching on
      // userId alone marks every listing selected and makes re-picking it a toggle.
      isOptionEqualToValue={(option, optionValue) => option.userId === optionValue.userId}
      getOptionDisabled={(option) => atCap && !selectedIds.has(option.userId)}
      onChange={(_event, next) => onChange(next)}
      slotProps={listAboveModal ? {
        popper: {
          sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
        },
      } : undefined}
      // getItemProps wires each chip into the Autocomplete's keyboard model
      // (ArrowLeft/Right move between chips, Backspace/Delete remove one).
      renderValue={(chosen, getItemProps) => chosen.map((option, index) => {
        const { key: _key, disabled: itemDisabled, ...itemProps } = getItemProps({ index });
        const pending = disabledUserIds?.has(option.userId) ?? false;
        return (
          <OwnerChip
            {...itemProps}
            key={option.userId}
            label={option.label}
            disabled={pending || Boolean(itemDisabled)}
            data-cy={chipDataCy?.(option) ?? `${id}-chip-${option.userId}`}
            deleteDataCy={deleteDataCy?.(option)}
            deleteAriaLabel={deleteAriaLabel?.(option) ?? `Remove ${option.label}`}
          />
        );
      })}
      renderInput={renderOwnerInput(
        "Owners",
        autoFocus,
        atCap ? `You can choose up to ${maxSelections} owners at once.` : undefined,
        required,
      )}
    />
  );
};

const SingleOwnerSelect: React.FC<SingleOwnerSelectProps> = ({
  options,
  value,
  onChange,
  loading = false,
  disabled = false,
  id = "assign-owner-select",
  autoFocus = false,
  grouped = true,
}) => (
  <Autocomplete<OwnerOption>
    id={id}
    options={options}
    loading={loading}
    value={value}
    disabled={disabled}
    getOptionLabel={(option) => option.label}
    getOptionKey={ownerOptionKey}
    groupBy={grouped ? (option) => option.group : undefined}
    isOptionEqualToValue={(option, optionValue) =>
      option.userId === optionValue.userId && option.group === optionValue.group
    }
    onChange={(_event, next) => onChange(next)}
    renderInput={renderOwnerInput("Owner", autoFocus)}
  />
);

const OwnerSelect: React.FC<OwnerSelectProps> = (props) =>
  (props.multiple ? <MultipleOwnerSelect {...props} /> : <SingleOwnerSelect {...props} />);

export default OwnerSelect;
